import { queryClient } from '@/data';
import { SBK_BETSLIP_LOG_TAG, initialState } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { eventKeys, getEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { AcceptMultiplierChanges, defaultUserSettings } from '@/hooks/use-auth-user-settings';
import { Currency } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { runStoreUpdate } from '@/utils/zustand';

import { BetType, SBKBetSlip, StakeInputError } from '../types';

/**
 * Remove all selections for betslip
 */
export const clearBetSlip = () => ({
    ...initialState,
});

/**
 * Resets betslip to initial state, but keeps the bet selections
 */
export const keepBetSlipSelections = (state: SBKBetSlip) => ({
    ...initialState,
    eventOrder: state.eventOrder,
    selectionOrder: state.selectionOrder,
    options: state.options,
    markets: state.markets,
    events: state.events,
    selections: state.selections,
    sgpEventDisabled: state.sgpEventDisabled,
    sgpOdds: state.sgpOdds,
});

/**
 * Toggle selection status. The status is the current state of the selection, either true (on) or false (off).
 */
export const toggleComboSelectionStatus = (state: SBKBetSlip, selectionId: string) => {
    const newState = { ...state };
    const hasSgpError = state.sgpEventDisabled[state.selections[selectionId].eventId];
    const isTogglingOn = !state.selections[selectionId].isComboEnabled;
    if (hasSgpError && isTogglingOn) {
        return forceToggleOnlySelection(newState, selectionId);
    } else {
        return {
            ...state,
            lastToggledSelectionId: selectionId,
            selections: {
                ...state.selections,
                [selectionId]: {
                    ...state.selections[selectionId],
                    isComboEnabled: !state.selections[selectionId].isComboEnabled,
                },
            },
            sgpTemporaryIssue: {
                ...state.sgpTemporaryIssue,
                [state.selections[selectionId].eventId]: false,
            },
        };
    }
};

const forceToggleOnlySelection = (state: SBKBetSlip, selectionId: string) => {
    const eventId = state.selections[selectionId].eventId;
    const eventSelections = Object.values(state.selections).filter(s => s.eventId === eventId);
    const newSelections = eventSelections.reduce((acc, s) => {
        return {
            ...acc,
            [s.id]: {
                ...s,
                isComboEnabled: s.id === selectionId,
            },
        };
    }, {});
    return {
        ...state,
        selections: {
            ...state.selections,
            ...newSelections,
        },
    };
};

/**
 * Update Stake
 */
export const updateStake = (
    state: SBKBetSlip,
    betId: string,
    stake: number,
    displayStake: string,
    betType: BetType
) => {
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                id: betId,
                betType,
                stake,
                displayStake,
                isBetrBucks: stake === 0 ? undefined : state.useBetrBucks, // when stake is 0, reset isBetrBucks
            },
        },
    };
};

/**
 * Update Stake Currency
 */
export const updateStakeCurrency = (state: SBKBetSlip, betId: string, currency: Currency) => {
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                isBetrBucks: currency === Currency.Fre,
            },
        },
    };
};

/**
 * Update Stake Input Errors
 */
export const updateStakeInputErrors = (state: SBKBetSlip, betId: string, stakeInputError?: StakeInputError) => {
    if (!state.bets[betId]) {
        return state;
    }
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                stakeInputError,
            },
        },
    };
};

type PayloadProps = {
    event_id: string;
    bets: {
        side_bet_id: string;
        side_bet_option_id: string;
    }[];
};

export const sgpOddsKeys = {
    all: ['sgpOdds'] as const,
    sgpOdds: (payload: PayloadProps) => [...sgpOddsKeys.all, payload] as const,
};

/**
 * For the two following cases:
 *  1. User has accepted all odds changes enabled
 *  2. User has higher odds changes enabled and the current odds change match the condition
 * The function will return the value to simulate show/hide animation for the odds change indicator for 2 seconds.
 * Otherwise it will return null (no animation show).
 */
export const getOddsChangeIndicatorTimeout = ({
    state,
    oddsChanges,
}: {
    state: SBKBetSlip;
    oddsChanges: { [x: string]: number };
}) => {
    let timeout = null;
    if (Object.keys(oddsChanges).length > 0) {
        if (state.oddsChangeTimeout) {
            clearTimeout(state.oddsChangeTimeout);
        }
        const { userSettings = defaultUserSettings } = state;
        timeout = setTimeout(() => {
            if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.ALL) {
                runStoreUpdate(() => state.actions.clearOddsChanges());
            }
            if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.HIGHER) {
                runStoreUpdate(() => state.actions.clearHigherOddsChanges());
            }
        }, 2000);
    }
    return timeout;
};

/**
 * Fetches an event using react-query
 * @param eventId - The event id
 */
export const getEventDetails = async (eventId: string) => {
    try {
        return await queryClient.fetchQuery({
            queryKey: eventKeys.byId(eventId),
            queryFn: () => getEvent(eventId),
        });
    } catch (e) {
        logger.warn(SBK_BETSLIP_LOG_TAG, `Failed to fetch event ${eventId}`, e);
    }
};
